/**
 * Fibes scroll reveal v2 — content fades in and rises as it enters the viewport.
 *
 * v2 model: we never reveal the .shopify-section wrapper itself. Instead each
 * section is broken into content UNITS (blocks, columns, cards) which reveal
 * individually, staggered left-to-right/top-to-bottom by DOM order. Section
 * backgrounds (.section-background, scheme colours painted on section roots)
 * therefore stay static — only content moves.
 *
 * Unit selection, per section:
 *   1. `.section-content-wrapper` children (all stock "Section" sections)
 *   2. else: root element's children (fibes and lander custom sections —
 *      their roots paint the bg, their children are header/grid/table units)
 *   3. known grid/track containers expand one level deeper so individual
 *      cards/blocks stagger (pages cards, benefits grid, hero strip,
 *      lander content blocks)
 *
 * Skipped entirely: first section (hero/LCP), dividers, marquees, sections
 * containing sticky elements, Loop portal (gated in theme.liquid), reduced
 * motion, theme editor.
 *
 * Companion CSS in base.css; settings in Theme settings → Animations.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (window.Shopify && window.Shopify.designMode) return;

  const main = document.getElementById('MainContent');
  if (!main) return;

  const SKIP_ID = /divider|marquee|sticky/i;
  const EXPAND =
    '.lander-content-blocks__inner, .fpcards-track, .fbg-grid, .fhs-grid';
  const IGNORE =
    'script, style, link, template, .section-background, .custom-section-background';
  const MAX_UNITS = 20;
  const SIBLING_STEP = 70; // ms, left-to-right stagger within a container
  const SIBLING_CAP = 6; // cap the multiplier so long grids don't lag
  const LOAD_STEP = 80; // ms, cascade for units already in view on load

  const unitsForSection = (section) => {
    // 1. Stock sections: block children of the content wrapper
    let container = section.querySelector(':scope .section-content-wrapper');

    // 2. Custom sections: children of the (non-background) root element
    if (!container) {
      const roots = [...section.children].filter((el) => !el.matches(IGNORE));
      if (roots.length !== 1) return roots; // 0 or several roots: reveal as-is
      container = roots[0];
    }

    let units = [...container.children].filter((el) => !el.matches(IGNORE));
    if (!units.length) return [container];

    // 3. Boxless wrappers: display:contents elements (stock Sections wrap
    // their blocks in .mobile-scroller-wrapper) generate no box, so
    // opacity/transform on them render nothing. Step through to children.
    const isBoxless = (el) =>
      el.matches('.mobile-scroller-wrapper') ||
      getComputedStyle(el).display === 'contents';
    for (let d = 0; d < 2 && units.some(isBoxless); d++) {
      units = units.flatMap((u) =>
        isBoxless(u) ? [...u.children].filter((el) => !el.matches(IGNORE)) : [u]
      );
      if (!units.length) return [container];
    }

    // 3b. Sections that boil down to ONE group block (very common: a group
    // wraps the whole section) step inside it so its blocks stagger,
    // instead of the group fading as a single slab.
    for (let depth = 0; depth < 2 && units.length === 1; depth++) {
      const only = units[0];
      if (!only.matches('.group-block')) break;
      const inner = only.querySelector(':scope > .group-block-content') || only;
      const kids = [...inner.children].filter((el) => !el.matches(IGNORE));
      if (!kids.length) break;
      units = kids;
    }

    // 4. Expand known grids/tracks one level so cards stagger individually
    units = units.flatMap((u) => {
      if (!u.matches(EXPAND)) return [u];
      const kids = [...u.children].filter((el) => !el.matches(IGNORE));
      return kids.length ? kids : [u];
    });

    return units.slice(0, MAX_UNITS);
  };

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('reveal-visible');
        io.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
  );

  const sections = [...main.querySelectorAll(':scope > .shopify-section')];
  const vh = window.innerHeight;
  let loadDelay = 0;

  try {
    sections.forEach((section, index) => {
      if (index === 0) return;
      if (SKIP_ID.test(section.id)) return;
      if (section.querySelector('[class*="sticky"]')) return;

      const rect = section.getBoundingClientRect();
      if (rect.bottom <= 0) return; // above viewport (scroll restore): leave alone

      for (const unit of unitsForSection(section)) {
        const siblingIndex = [...unit.parentElement.children].indexOf(unit);
        const siblingDelay = Math.min(siblingIndex, SIBLING_CAP) * SIBLING_STEP;

        unit.classList.add('reveal-pending');

        if (rect.top < vh) {
          // Section visible on load: quick global cascade
          unit.style.setProperty('--reveal-delay', `${loadDelay}ms`);
          loadDelay += LOAD_STEP;
          requestAnimationFrame(() =>
            requestAnimationFrame(() => unit.classList.add('reveal-visible'))
          );
        } else {
          // Below the fold: reveal on entry, offset by position in its row
          unit.style.setProperty('--reveal-delay', `${siblingDelay}ms`);
          io.observe(unit);
        }
      }
    });
  } catch (err) {
    // Never leave content hidden: strip any pre-hide state and bail.
    document
      .querySelectorAll('.reveal-pending')
      .forEach((el) => el.classList.remove('reveal-pending'));
  } finally {
    // Release the pre-paint hide set by the inline <head> marker script.
    document.documentElement.classList.remove('reveal-init');
  }
})();